import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { COLORS } from '../../../core/constants/colors';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useSectors } from '../../admin/companies/hooks/useSectors';
import {
  ProgressSidebar,
  OnboardingStep,
  UploadStep,
  ProcessingStep,
  FormStep,
  FinalizationStep,
  CompletionStep,
  EnrichmentOverviewStep,
} from '../components';
import { INITIAL_FORM_STATE } from '../constants';
import { makePartnerCompanySource, partnerCompanySource } from '../companySources';
import { useEnrichmentFlow } from '../hooks';

const PARTNER_FEATURES = {
  markEnrichmentCompleteOnFinalize: true,
  writeLocalEnrichmentStatusOnSkip: true,
  completeAndExitOnSkip: true,
  enableOverviewStep: true,
};

export const EnrichmentFlowPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useParams<{ companyId?: string }>();
  const { refreshUser, authState } = useAuth();
  const { sectors, createSector } = useSectors();

  // Page-only: suppress effects during the brief window between a skip-redirect
  // starting and the page actually unloading. This belongs in the page, not
  // the hook, because it guards the page-level enrichment-status redirect.
  const skipInProgress = useRef(false);

  const prefilledCompanyName = (location.state as any)?.prefilledCompanyName as
    | string
    | undefined;
  const numericCompanyId = useMemo(() => {
    if (!companyId) return null;
    const parsed = Number.parseInt(companyId, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [companyId]);
  const companySource = useMemo(
    () => (numericCompanyId ? makePartnerCompanySource(numericCompanyId) : partnerCompanySource),
    [numericCompanyId],
  );

  // Partner-only enrichment-status redirect (belt-and-braces with ProtectedRoute).
  // If the user has finished enrichment and isn't on the /dashboard/enrichment
  // re-run route, send them to the dashboard.
  useEffect(() => {
    if (skipInProgress.current) return;
    const isDashboardEnrichmentRoute = location.pathname.startsWith('/dashboard/enrichment');
    const enrichmentStatus =
      authState.user?.enrichment_status || localStorage.getItem('enrichmentStatus');
    if (enrichmentStatus === 'completed' && !isDashboardEnrichmentRoute) {
      navigate('/dashboard', { replace: true });
    }
  }, [authState.user?.enrichment_status, navigate, location.pathname]);

  const flow = useEnrichmentFlow({
    mode: 'partner',
    companySource,
    features: PARTNER_FEATURES,
    onAfterFinalize: async () => {
      // Hard redirect: forces a full reload that re-runs auth state from scratch
      // so the dashboard sees the new enrichment_status.
      window.location.href = '/dashboard';
    },
    onAfterSkipToCompletion: async () => {
      skipInProgress.current = true;
      window.location.href = '/dashboard';
    },
    onAuthStateMayHaveChanged: refreshUser,
    getFreshFormState: () =>
      prefilledCompanyName
        ? { ...INITIAL_FORM_STATE, name: prefilledCompanyName }
        : INITIAL_FORM_STATE,
  });

  if (flow.companyLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.extended.cultured }}
      >
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full border-4 border-t-transparent mx-auto animate-spin"
            style={{ borderColor: COLORS.primary.persianIndigo }}
          />
          <p className="mt-4 text-sm" style={{ color: COLORS.primary.persianIndigo }}>
            Loading company data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.extended.cultured }}>
      <div className="flex-shrink-0 w-72 pl-10 pr-8 py-12">
        <ProgressSidebar steps={flow.progressSteps} />
      </div>

      <div className="flex-1 bg-white h-screen overflow-y-auto">
        <div className="max-w-4xl mx-auto px-12 py-12">
          {flow.flowStep === 'overview' && flow.company && (
            <EnrichmentOverviewStep
              company={flow.company}
              onRerun={() => flow.setFlowStep('upload')}
              onBack={() => navigate('/dashboard')}
            />
          )}

          {flow.flowStep === 'onboarding' && (
            <OnboardingStep
              companyForm={flow.companyForm}
              onInputChange={flow.handleCompanyInputChange}
              onSectorChange={flow.handleSectorChange}
              onSave={flow.handleSaveCompany}
              saving={flow.savingCompany}
              error={flow.companyError}
              sectors={sectors}
              onCreateSector={createSector}
            />
          )}

          {flow.flowStep === 'upload' && (
            <UploadStep
              company={flow.company}
              existingDocuments={flow.existingDocuments}
              selectedExistingDocumentIds={flow.selectedExistingDocumentIds}
              loadingDocuments={flow.loadingDocuments}
              onToggleExistingDocument={flow.handleToggleExistingDocument}
              onUploadComplete={flow.handleInitialUploadComplete}
              onStartEnrichment={flow.handleStartEnrichment}
              onBack={() => flow.setFlowStep('onboarding')}
              readyToStart={flow.readyToStart}
              startingSession={flow.startingSession}
              sessionError={flow.sessionError}
              existingSessionConflict={flow.existingSessionConflict}
              onViewExistingSession={flow.handleViewExistingSession}
              onSkipExistingSession={flow.handleSkipExistingSession}
              onCancelExistingSession={flow.handleCancelExistingSession}
              onCloseConflictModal={flow.closeConflictModal}
            />
          )}

          {flow.flowStep === 'processing' && (
            <ProcessingStep
              enrichmentCompleted={flow.enrichmentCompleted}
              processingAfterSubmit={flow.processingAfterSubmit}
              processingMessage={flow.processingMessage}
              session={flow.session}
              sessionError={flow.sessionError}
              checkingStatus={flow.checkingStatus}
              loadingForm={flow.loadingForm}
              documentCount={
                flow.selectedExistingDocumentIds.length + flow.initialDocumentIds.length
              }
              onBack={() => flow.setFlowStep('upload')}
              onViewSession={flow.handleViewSessionWhileWaiting}
              onSkipEnrichment={flow.handleSkipEnrichment}
              onCancelSession={flow.handleCancelFromProcessing}
              onLoadForm={flow.handleLoadForm}
              onSkipToDashboard={async () => {
                skipInProgress.current = true;
                await flow.handleSkipToDashboardFromProcessing();
              }}
            />
          )}

          {flow.flowStep === 'form' && (
            <FormStep
              session={flow.session}
              formData={flow.formData}
              fieldResponses={flow.fieldResponses}
              additionalDocumentIds={flow.additionalDocumentIds}
              company={flow.company}
              submittingResponses={flow.submittingResponses}
              sessionError={flow.sessionError}
              responseMessage={flow.responseMessage}
              onBack={() => flow.setFlowStep('processing')}
              onFieldChange={flow.handleFieldChange}
              onAdditionalUploadComplete={flow.handleAdditionalUploadComplete}
              onSubmitResponses={flow.handleSubmitResponses}
              onSkipEnrichment={flow.handleSkipEnrichment}
            />
          )}

          {flow.flowStep === 'finalization' && (
            <FinalizationStep
              finalizeMessage={flow.finalizeMessage}
              finalizing={flow.finalizing}
              onBack={() => flow.setFlowStep('form')}
              onFinalize={flow.handleFinalize}
            />
          )}

          {flow.flowStep === 'completed' && <CompletionStep />}
        </div>
      </div>
    </div>
  );
};
